import { useState, useRef, useEffect } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { ThumbUpAltOutlined, CloseRounded } from "@mui/icons-material";
import Cookies from "js-cookie";

import HelpTooltip from "../../components/CallScreen/HelpTooltip";
import { detectMediaAccess } from "../../utils";
import theme from "../../Theme";
import DefaultButton from "./DefaultButton";

// ─── Design tokens ────────────────────────────────────────────────────────────
const TOKEN = {
  off: {
    background: "#02002B",
    stroke: "#01ECFF",
  },
  on: {
    background: "transparent",
    stroke: "#FEFEFE",
  },
};

// ─── Shared icon button style factory ─────────────────────────────────────────
const getIconButtonSx = ({ isOff = false } = {}) => ({
  minWidth: "none",
  borderRadius: "50%",
  padding: "10px",
  transition: "background-color 0.15s ease, color 0.15s ease",
  "&.MuiIconButton-root": {
    backgroundColor: isOff ? TOKEN.off.background : "transparent",
    color: isOff ? TOKEN.off.stroke : TOKEN.on.stroke,
  },
  "&.MuiIconButton-root:hover": {
    backgroundColor: isOff ? TOKEN.off.background : "rgba(255,255,255,0.12)",
    color: isOff ? TOKEN.off.stroke : TOKEN.on.stroke,
  },
  [theme.breakpoints.down("md")]: {
    "&.MuiIconButton-root": {
      backgroundColor: isOff ? TOKEN.off.background : "rgba(255,255,255,0.15)",
      padding: "14px",
    },
  },
});

// ─── SVG helpers ──────────────────────────────────────────────────────────────

const CameraIcon = ({ isOff }) => {
  const stroke = isOff ? TOKEN.off.stroke : TOKEN.on.stroke;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {isOff ? (
        <g clipPath="url(#cam-off-clip)">
          <path
            d="M4.16699 4.16675C2.78628 4.16675 1.66699 5.28604 1.66699 6.66675V13.3334C1.66699 14.7141 2.78628 15.8334 4.16699 15.8334H11.667C12.7942 15.8334 13.7472 15.0874 14.0591 14.0622M14.167 10.0001L17.1956 6.97149C17.5526 6.61451 17.7311 6.43602 17.8843 6.42396C18.0173 6.41349 18.1472 6.46731 18.2338 6.56873C18.3337 6.68562 18.3337 6.93804 18.3337 7.44289V12.5573C18.3337 13.0621 18.3337 13.3145 18.2338 13.4314C18.1472 13.5329 18.0173 13.5867 17.8843 13.5762C17.7311 13.5641 17.5526 13.3857 17.1956 13.0287L14.167 10.0001ZM14.167 10.0001V8.16675C14.167 6.76662 14.167 6.06655 13.8945 5.53177C13.6548 5.06137 13.2724 4.67892 12.802 4.43923C12.2672 4.16675 11.5671 4.16675 10.167 4.16675H7.91699M1.66699 1.66675L18.3337 18.3334"
            stroke={stroke}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      ) : (
        <>
          <path
            d="M18.3332 7.44265C18.3332 6.9378 18.3332 6.68538 18.2333 6.56849C18.1467 6.46707 18.0168 6.41325 17.8838 6.42371C17.7306 6.43577 17.5521 6.61426 17.1951 6.97124L14.1665 9.99984L17.1951 13.0284C17.5521 13.3854 17.7306 13.5639 17.8838 13.576C18.0168 13.5864 18.1467 13.5326 18.2333 13.4312C18.3332 13.3143 18.3332 13.0619 18.3332 12.557V7.44265Z"
            stroke={stroke}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M1.6665 8.1665C1.6665 6.76637 1.6665 6.06631 1.93899 5.53153C2.17867 5.06112 2.56112 4.67867 3.03153 4.43899C3.56631 4.1665 4.26637 4.1665 5.6665 4.1665H10.1665C11.5666 4.1665 12.2667 4.1665 12.8015 4.43899C13.2719 4.67867 13.6543 5.06112 13.894 5.53153C14.1665 6.06631 14.1665 6.76637 14.1665 8.1665V11.8332C14.1665 13.2333 14.1665 13.9334 13.894 14.4681C13.6543 14.9386 13.2719 15.321 12.8015 15.5607C12.2667 15.8332 11.5666 15.8332 10.1665 15.8332H5.6665C4.26637 15.8332 3.56631 15.8332 3.03153 15.5607C2.56112 15.321 2.17867 14.9386 1.93899 14.4681C1.6665 13.9334 1.6665 13.2333 1.6665 11.8332V8.1665Z"
            stroke={stroke}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      )}
      {isOff && (
        <defs>
          <clipPath id="cam-off-clip">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      )}
    </svg>
  );
};

const CCIcon = ({ isOff }) => {
  const stroke = isOff ? TOKEN.off.stroke : TOKEN.on.stroke;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.6665 6.66667C1.6665 5.26654 1.6665 4.56647 1.93899 4.03169C2.17867 3.56129 2.56112 3.17883 3.03153 2.93915C3.56631 2.66667 4.26637 2.66667 5.6665 2.66667H14.3332C15.7333 2.66667 16.4334 2.66667 16.9681 2.93915C17.4386 3.17883 17.821 3.56129 18.0607 4.03169C18.3332 4.56647 18.3332 5.26654 18.3332 6.66667V11.3333C18.3332 12.7335 18.3332 13.4335 18.0607 13.9683C17.821 14.4387 17.4386 14.8212 16.9681 15.0608C16.4334 15.3333 15.7333 15.3333 14.3332 15.3333H10.8332L7.49984 18V15.3333H5.6665C4.26637 15.3333 3.56631 15.3333 3.03153 15.0608C2.56112 14.8212 2.17867 14.4387 1.93899 13.9683C1.6665 13.4335 1.6665 12.7335 1.6665 11.3333V6.66667Z"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 8.33333C7.5 7.87309 7.1269 7.5 6.6665 7.5C6.00346 7.5 5.4165 8.08696 5.4165 9C5.4165 9.91304 6.00346 10.5 6.6665 10.5C7.1269 10.5 7.5 10.1269 7.5 9.66667"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <path
        d="M12.5 8.33333C12.5 7.87309 12.1269 7.5 11.6665 7.5C11.0035 7.5 10.4165 8.08696 10.4165 9C10.4165 9.91304 11.0035 10.5 11.6665 10.5C12.1269 10.5 12.5 10.1269 12.5 9.66667"
        stroke={stroke}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {isOff && (
        <line x1="2" y1="2" x2="18" y2="18" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
      )}
    </svg>
  );
};

const TranscriptIcon = ({ isActive }) => {
  const stroke = TOKEN.on.stroke;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.5 15L16.6666 15.9117C16.2245 16.3951 15.6251 16.6667 15.0001 16.6667C14.3751 16.6667 13.7757 16.3951 13.3337 15.9117C12.891 15.4293 12.2916 15.1584 11.6668 15.1584C11.042 15.1584 10.4426 15.4293 9.99998 15.9117M2.5 16.6667H3.89545C4.3031 16.6667 4.50693 16.6667 4.69874 16.6206C4.8688 16.5798 5.03138 16.5125 5.1805 16.4211C5.34869 16.318 5.49282 16.1739 5.78107 15.8856L16.25 5.41669C16.9404 4.72634 16.9404 3.60705 16.25 2.91669C15.5597 2.22634 14.4404 2.22634 13.75 2.91669L3.28105 13.3856C2.9928 13.6739 2.84867 13.818 2.7456 13.9862C2.65422 14.1353 2.58688 14.2979 2.54605 14.468C2.5 14.6598 2.5 14.8636 2.5 15.2713V16.6667Z"
        stroke={stroke}
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const HelpIcon = ({ isOff }) => {
  const stroke = isOff ? TOKEN.off.stroke : TOKEN.on.stroke;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#help-clip)">
        <path
          d="M7.57533 7.50008C7.77125 6.94314 8.15795 6.4735 8.66695 6.17436C9.17596 5.87521 9.77441 5.76586 10.3563 5.86567C10.9382 5.96549 11.466 6.26802 11.8462 6.71969C12.2264 7.17136 12.4345 7.74302 12.4337 8.33342C12.4337 10.0001 9.93366 10.8334 9.93366 10.8334M10.0003 14.1667H10.0087M18.3337 10.0001C18.3337 14.6025 14.6027 18.3334 10.0003 18.3334C5.39795 18.3334 1.66699 14.6025 1.66699 10.0001C1.66699 5.39771 5.39795 1.66675 10.0003 1.66675C14.6027 1.66675 18.3337 5.39771 18.3337 10.0001Z"
          stroke={stroke}
          strokeWidth="1.66667"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {isOff && (
          <line x1="2" y1="2" x2="18" y2="18" stroke={stroke} strokeWidth="1.8" strokeLinecap="round" />
        )}
      </g>
      <defs>
        <clipPath id="help-clip">
          <rect width="20" height="20" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const ResourcesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.6663 1.89136V5.33348C11.6663 5.80018 11.6663 6.03354 11.7572 6.2118C11.8371 6.3686 11.9645 6.49609 12.1213 6.57598C12.2996 6.66681 12.533 6.66681 12.9997 6.66681H16.4418M13.333 10.8334H6.66634M13.333 14.1667H6.66634M8.33301 7.50008H6.66634M11.6663 1.66675H7.33301C5.93288 1.66675 5.23281 1.66675 4.69803 1.93923C4.22763 2.17892 3.84517 2.56137 3.60549 3.03177C3.33301 3.56655 3.33301 4.26662 3.33301 5.66675V14.3334C3.33301 15.7335 3.33301 16.4336 3.60549 16.9684C3.84517 17.4388 4.22763 17.8212 4.69803 18.0609C5.23281 18.3334 5.93288 18.3334 7.33301 18.3334H12.6663C14.0665 18.3334 14.7665 18.3334 15.3013 18.0609C15.7717 17.8212 16.1542 17.4388 16.3939 16.9684C16.6663 16.4336 16.6663 15.7335 16.6663 14.3334V6.66675L11.6663 1.66675Z"
      stroke={TOKEN.on.stroke}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const FullscreenIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.6667 8.33333L17.5 2.5M17.5 2.5H12.5M17.5 2.5V7.5M8.33333 11.6667L2.5 17.5M2.5 17.5H7.5M2.5 17.5L2.5 12.5"
      stroke={TOKEN.on.stroke}
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const CallToolbar = ({
  videoEnabled,
  handleToggleVideo,
  handleEndSimulation,
  openHelpModal,
  panelValue,
  showSidePanel,
  setPanelValue,
  openSidePanel,
  scenarioData,
  isExternalMode,
  isMobile,
  isSpeaking,
  onHelpTooltipClose,
  isFeedbackSession,
  handleToggleFullscreen,
  isFullscreen,
  subTitlesEnabled,
  handleToggleSubtitles,
  simulationType,
  /** Whether the call is running in audio-only fallback mode */
  isAudioOnly = false,
  /** Callback for the "Try Video" button — user-initiated attempt to return to video */
  onTryVideo,
}) => {
  const toolbarRef = useRef(null);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [hasMediaAccess, setHasMediaAccess] = useState({});
  const [hasCompletedSim, setHasCompletedSim] = useState(false);
  const [, setTick] = useState(0);

  const forceUpdate = () => setTick((t) => t + 1);

  useEffect(() => {
    if (!Cookies.get("GN-CompletedSim")) {
      if (!isMobile) setShowHelpTooltip(true);
      const isSecure = window.location.protocol === "https:";
      Cookies.set("GN-CompletedSim", "false", {
        expires: 365,
        secure: isSecure,
        sameSite: isSecure ? "None" : "Lax",
      });
    }

    (async () => {
      const access = await detectMediaAccess();
      setHasMediaAccess(access);
    })();
  }, []);

  useEffect(() => {
    if (
      window.location.href ===
        "https://app.gurunow.ai/externalscenario/4fdb30f6-d89f-4af3-a56c-ae10c949cb43" ||
      window.location.href ===
        "http://localhost:3000/externalscenario/6d8a6c79-9836-482e-a9e6-8a2d1964fcec"
    ) {
      setShowHelpTooltip(false);
      return;
    }

    let lastAccess = {};
    let lastCookie = Cookies.get("GN-CompletedSim");

    const check = async () => {
      const access = await detectMediaAccess();
      const cookieValue = Cookies.get("GN-CompletedSim");
      const changed =
        access.camera !== lastAccess.camera ||
        access.microphone !== lastAccess.microphone ||
        cookieValue !== lastCookie;

      if (changed) {
        setHasMediaAccess(access);
        setHasCompletedSim(cookieValue);
        forceUpdate();
        lastAccess = access;
        lastCookie = cookieValue;
      }
    };

    const interval = setInterval(check, 3000);
    check();
    return () => clearInterval(interval);
  }, []);

  const handleOpenTab = (value) => {
    setPanelValue(value);
    openSidePanel();
  };

  const isPanelOpen = (tabIndex) => panelValue === tabIndex && showSidePanel;

  const recommendationsHidden =
    isExternalMode || !scenarioData?.has_scoring_engine;
  const resourcesExist = scenarioData?.resources !== null;

  return (
    <Box
      id="call-toolbar"
      ref={toolbarRef}
      sx={{
        width: "fit-content",
        height: "72px",
        borderRadius: 999,
        backgroundColor: isAudioOnly
          ? "rgba(255, 255, 255, 0.13)"   // lighter glass — off-state icon bg (#02002B) visible against audio-only dark navy
          : "rgba(0, 0, 0, 0.38)",         // standard dark glass for video mode
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 20px",
        // Always visible — no auto-hide
        opacity: 1,
        pointerEvents: "auto",
        backdropFilter: "blur(10px)",
        [theme.breakpoints.down("md")]: {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "12px",
          height: "auto",
          width: "auto",
          padding: "12px 16px",
          flexWrap: "wrap",
        },
        "& img": {
          [theme.breakpoints.down("md")]: {
            width: "20px",
            height: "20px",
          },
        },
        "& .scenario-name": {
          marginBottom: "12px",
          fontSize: "14px",
          color: "#ffffff",
          fontWeight: "bold",
          background: "rgba(0,0,0,.7)",
          textAlign: "center",
          width: "100%",
          borderRadius: "12px",
          padding: "10px 15px",
        },
        "& .toolbar-actions": {
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        },
      }}
    >
      {isMobile && simulationType !== "feedback_session" && (
        <div className="scenario-name">{scenarioData?.name}</div>
      )}

      <div className="toolbar-actions">
        {/* ── Closed Captions ──────────────────────────────────────────────── */}
        <Tooltip title={subTitlesEnabled ? "Subtitles ON" : "Subtitles OFF"} placement="top" arrow>
          <IconButton
            onClick={handleToggleSubtitles}
            sx={getIconButtonSx({ isOff: !subTitlesEnabled })}
            aria-pressed={subTitlesEnabled}
            aria-label="Toggle subtitles"
          >
            <CCIcon isOff={!subTitlesEnabled} />
          </IconButton>
        </Tooltip>

        {/* ── Camera — hidden in audio-only mode ───────────────────────────── */}
        {!isAudioOnly && (
          <Tooltip title={videoEnabled ? "Camera ON" : "Camera OFF"} placement="top" arrow>
            <IconButton
              onClick={handleToggleVideo}
              sx={getIconButtonSx({ isOff: !videoEnabled })}
              aria-pressed={videoEnabled}
              aria-label="Toggle camera"
            >
              <CameraIcon isOff={!videoEnabled} />
            </IconButton>
          </Tooltip>
        )}

        {/* ── Fullscreen (feedback_session only) ───────────────────────────── */}
        {simulationType === "feedback_session" && (
          <Tooltip title="Toggle Fullscreen" placement="top" arrow>
            <IconButton
              sx={getIconButtonSx({ isOff: false })}
              onClick={handleToggleFullscreen}
              aria-label="Toggle fullscreen"
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* ── Panel buttons (non-feedback sessions only) ────────────────────── */}
        {simulationType !== "feedback_session" && (
          <>
            <Tooltip title="Transcript" placement="top" arrow>
              <IconButton
                onClick={() => handleOpenTab(0)}
                sx={getIconButtonSx({ isActive: isPanelOpen(0) })}
                aria-pressed={isPanelOpen(0)}
                aria-label="Open transcript panel"
              >
                <TranscriptIcon isActive={isPanelOpen(0)} />
              </IconButton>
            </Tooltip>

            {!recommendationsHidden && (
              <Tooltip title="Recommendations" placement="top" arrow>
                <IconButton
                  onClick={() => handleOpenTab(1)}
                  sx={getIconButtonSx({ isActive: isPanelOpen(1) })}
                  aria-pressed={isPanelOpen(1)}
                  aria-label="Open recommendations panel"
                >
                  <ThumbUpAltOutlined
                    sx={{ color: isPanelOpen(1) ? TOKEN.on.stroke : TOKEN.on.stroke }}
                  />
                </IconButton>
              </Tooltip>
            )}

            {resourcesExist && simulationType !== "on_demand_coach" && (
              <Tooltip title="Resources" placement="top" arrow>
                <IconButton
                  onClick={() => handleOpenTab(1)}
                  sx={getIconButtonSx({ isActive: isPanelOpen(1) })}
                  aria-pressed={isPanelOpen(1)}
                  aria-label="Open resources panel"
                >
                  <ResourcesIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip
              open={showHelpTooltip}
              title={
                <HelpTooltip
                  isNewUser={hasCompletedSim}
                  mediaAccess={hasMediaAccess}
                  handleClose={() => {
                    setShowHelpTooltip(false);
                    onHelpTooltipClose();
                  }}
                />
              }
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    maxWidth: "none",
                    color: "#030039",
                    background: "#030039",
                    borderRadius: "8px",
                    "& span:before": { background: "#030039" },
                  },
                },
              }}
              placement="top"
            >
              <IconButton
                onClick={() => setShowHelpTooltip((prev) => !prev)}
                sx={getIconButtonSx({ isOff: !showHelpTooltip })}
                aria-pressed={showHelpTooltip}
                aria-label="Help"
              >
                <HelpIcon isOff={!showHelpTooltip} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* ── End Simulation ────────────────────────────────────────────────── */}
        <DefaultButton
          variant="contained"
          textColor="#fff"
          startIcon={<CloseRounded sx={{ fontSize: "16px" }} />}
          borderColor="transparent"
          onClick={handleEndSimulation}
          sx={{
            color: "#fff",
            backgroundColor: "#d82c21",
            minWidth: isMobile ? "40px" : "148px",
            px: isMobile ? "10px" : "16px",
            flexShrink: 0,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "#b71c1c" },
            [theme.breakpoints.down("md")]: {
              minWidth: "40px",
              px: "10px",
            },
          }}
        >
          {!isMobile ? "End Simulation" : "Close"}
        </DefaultButton>
      </div>
    </Box>
  );
};

export default CallToolbar;
